import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useIntl } from 'umi';
import omit from 'lodash/omit';
import {
  createCustomerContactRequest,
  deleteCustomerContactRequest,
  fetchLeadContactsRequest,
  setLeadPrimaryContactRequest,
  updateCustomerContactRequest,
} from '@/services/api/contact';
import { processAPIResponseNow } from '@/utils/request';
import { getIntlTranslation, INTL_ACTION } from '@/utils/intl';
import { CONTACT_ENTITY } from '@/utils/contacts';
import { ContactLinkPayload, ContactModel, ContactPayload } from '@/typings/models/Contact';
import { LeadDetailsModel } from '@/typings/models/Lead';
import { ContactFieldName } from '@/components/Contacts/ContactFields';

/** Fields the customer row mirrors from its primary contact (editable with `leads_edit`). */
const CUSTOMER_MIRRORED_FIELDS: ContactFieldName[] = ['firstname', 'lastname', 'email', 'phone', 'locale'];

type ApiCall = Promise<any>;

/** Resolves with the response data on 2xx, `undefined` otherwise (the helper already toasted). */
const settle = <T>(call: ApiCall, successMessage?: string) =>
  call.then(
    (response) =>
      new Promise<T | undefined>((resolve) => {
        let result: T | undefined;
        processAPIResponseNow(
          response,
          {
            successMessage,
            onSuccess: (data: T) => {
              result = data;
            },
          },
          'data',
        );
        resolve(result);
      }),
  );

export type AttachOptions = { role?: string | null; makePrimary?: boolean };

/**
 * Contact actions of one project. A project's contacts are its customer's, so
 * adding links a contact to the customer and removing unlinks it — on every
 * project of the customer. Only the project's primary is its own. The list is
 * reloaded after each change and goes straight into the store; `onChanged`
 * refreshes the rest (activities, linked projects).
 */
export const useLeadContacts = (lead: LeadDetailsModel, onChanged?: () => void) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const leadId = lead.id as string;
  const customerId = lead.customer?.id;
  const contacts = useMemo(() => lead.contacts || [], [lead.contacts]);
  const [busy, setBusy] = useState(false);

  const track = useCallback(async <T>(work: () => Promise<T>) => {
    setBusy(true);
    try {
      return await work();
    } finally {
      setBusy(false);
    }
  }, []);

  const storeContacts = useCallback(
    (list: ContactModel[] | undefined, primaryContactId?: string) => {
      if (!list) return;
      dispatch({ type: 'leads/saveLeadContacts', payload: { leadId, contacts: list, primaryContactId } });
      onChanged?.();
    },
    [dispatch, leadId, onChanged],
  );

  /** After a change on the customer: the project's list, made the project's primary first when asked. */
  const reload = useCallback(
    async (makePrimaryId?: string) => {
      const list = makePrimaryId
        ? await settle<ContactModel[]>(setLeadPrimaryContactRequest({ params: { leadId }, contactId: makePrimaryId }))
        : undefined;
      const fresh = list || (await settle<ContactModel[]>(fetchLeadContactsRequest({ params: { leadId } })));
      storeContacts(fresh, list ? makePrimaryId : undefined);
    },
    [leadId, storeContacts],
  );

  const setPrimary = useCallback(
    (contact: ContactModel) =>
      track(async () => {
        const list = await settle<ContactModel[]>(
          setLeadPrimaryContactRequest({ params: { leadId }, contactId: contact.id }),
        );
        storeContacts(list, contact.id);
        return Boolean(list);
      }),
    [leadId, storeContacts, track],
  );

  /** Unlinks from the customer; the backend refuses the customer's primary. */
  const remove = useCallback(
    (contact: ContactModel) =>
      track(async () => {
        if (!customerId) return false;
        const response = await deleteCustomerContactRequest({ params: { customerId, contactId: contact.id } });
        let removed = false;
        processAPIResponseNow(response, {
          successMessage: getIntlTranslation(intl, { entity: CONTACT_ENTITY, action: INTL_ACTION.REMOVE_SUCCESS }),
          onSuccess: () => {
            removed = true;
          },
        });
        if (removed) await reload();
        return removed;
      }),
    [customerId, intl, reload, track],
  );

  const saveContact = useCallback(
    async (contact: ContactModel, data: ContactPayload) => {
      if (!customerId) return undefined;
      const saved = await settle<ContactModel>(
        updateCustomerContactRequest({ params: { customerId, contactId: contact.id }, data }),
      );
      if (saved) dispatch({ type: 'leads/updateLeadContact', payload: { leadId, contact: saved } });
      return saved;
    },
    [customerId, dispatch, leadId],
  );

  /** Links a contact of the partner (e.g. someone at another customer) to this customer. */
  const attach = useCallback(
    (contact: ContactModel, { role, makePrimary }: AttachOptions = {}) =>
      track(async () => {
        if (!customerId) return false;
        const data: ContactLinkPayload = { contact_id: contact.id };
        // A role left out keeps the link's role.
        if (role !== undefined) data.role = role || null;
        const linked = await settle<ContactModel>(
          createCustomerContactRequest({ params: { customerId }, data }),
          getIntlTranslation(intl, { entity: CONTACT_ENTITY, action: INTL_ACTION.ADD_SUCCESS }),
        );
        if (!linked) return false;
        await reload(makePrimary ? linked.id : undefined);
        return true;
      }),
    [customerId, intl, reload, track],
  );

  /** A new contact of the customer, so on this project (and the customer's others). */
  const createAndAttach = useCallback(
    (data: ContactPayload, { makePrimary }: AttachOptions = {}) =>
      track(async () => {
        if (!customerId) return null;
        // `is_primary` here would make it the *customer's* primary: the project's goes separately.
        const created = await settle<ContactModel>(
          createCustomerContactRequest({ params: { customerId }, data: omit(data, 'is_primary') }),
          getIntlTranslation(intl, { entity: CONTACT_ENTITY, action: INTL_ACTION.CREATE_SUCCESS }),
        );
        if (!created) return null;
        await reload(makePrimary ? created.id : undefined);
        return created.id;
      }),
    [customerId, intl, reload, track],
  );

  /**
   * One field of a contact. Personal fields of the customer's primary contact
   * go through the project like before (only `leads_edit` needed); everything
   * else is written on the contact itself.
   */
  const updateField = useCallback(
    (contact: ContactModel, field: ContactFieldName, value: string | null) => {
      if (field === 'company_name' || (contact.is_primary && CUSTOMER_MIRRORED_FIELDS.includes(field))) {
        if (field !== 'company_name') {
          dispatch({ type: 'leads/updateLeadContact', payload: { leadId, contact: { ...contact, [field]: value } } });
        }
        dispatch({ type: 'leads/edit', payload: { leadId, [`customer.${field}`]: value } });
        return;
      }
      saveContact(contact, { [field]: value });
    },
    [dispatch, leadId, saveContact],
  );

  return {
    contacts,
    busy,
    setPrimary,
    remove,
    attach,
    createAndAttach,
    updateField,
  };
};
